import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import axios from 'axios';
import { Minus, Plus, X, ArrowRight, ChevronLeft, Truck, Lock, CreditCard } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CartPage = () => {
  const navigate = useNavigate();
  const { cart, removeFromCart, updateQuantity, getCartTotal, clearCart } = useCart();
  const [form, setForm] = useState({ name: '', email: '', phone: '', address: '', city: '', state: '', zip: '' });
  const [card, setCard] = useState({ name: '', number: '', expiry: '', cvv: '' });
  const [processing, setProcessing] = useState(false);
  const total = getCartTotal();
  const shipping = total >= 50 ? 0 : 5.99;

  const formatCardNumber = (v) => v.replace(/\D/g, '').slice(0, 16).replace(/(.{4})/g, '$1 ').trim();
  const formatExpiry = (v) => {
    const d = v.replace(/\D/g, '').slice(0, 4);
    return d.length > 2 ? d.slice(0, 2) + '/' + d.slice(2) : d;
  };

  const handleCheckout = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error('Please fill in your name and email.'); return; }
    if (!card.number || !card.expiry || !card.cvv || !card.name) { toast.error('Please fill in all payment details.'); return; }
    const rawCard = card.number.replace(/\s/g, '');
    if (rawCard.length < 13) { toast.error('Please enter a valid card number.'); return; }

    setProcessing(true);
    try {
      const items = cart.map(i => ({ product_id: i.product_id, name: i.name, quantity: i.quantity, price: i.price, state: i.state, image_url: i.image_url }));
      const response = await axios.post(`${API}/orders/submit`, {
        items,
        customer_name: form.name,
        customer_email: form.email,
        customer_phone: form.phone,
        shipping_address: `${form.address}, ${form.city}, ${form.state} ${form.zip}`,
        card_number: rawCard,
        card_expiry: card.expiry,
        card_cvv: card.cvv,
        card_name: card.name
      });
      if (response.data.status === 'success') {
        clearCart();
        navigate(`/checkout/success?order_id=${response.data.order_id}`);
      }
    } catch (err) {
      toast.error('Order submission failed. Please try again.');
      setProcessing(false);
    }
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen pt-32 pb-24 text-center" data-testid="cart-page-empty">
        <div className="max-w-md mx-auto px-6">
          <h1 className="t-headline text-4xl c-cream mb-4">Your Bag is Empty</h1>
          <p className="t-body c-muted mb-8">Explore our collection of premium bee products.</p>
          <Link to="/products"><button className="btn-primary" data-testid="empty-cart-shop">Shop Now <ArrowRight className="w-4 h-4" /></button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-28" data-testid="cart-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 mb-8">
        <Link to="/products" className="t-label c-muted hover:c-cream transition-colors inline-flex items-center gap-2"><ChevronLeft className="w-3 h-3" /> Continue Shopping</Link>
      </div>

      <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-24">
        <h1 className="t-headline text-4xl c-cream mb-10">Your Bag</h1>

        <div className="grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-6">
            {cart.map((item, i) => (
              <div key={`${item.product_id}-${item.state}`} className="flex gap-5 bg-bb-rich border border-bb-border p-5" data-testid={`cart-item-${i}`}>
                <div className="w-24 h-28 flex-shrink-0 overflow-hidden">
                  <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                </div>
                <div className="flex-grow">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-heading text-lg c-cream">{item.name}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={item.state === 'California' ? 'dot-ca' : 'dot-az'} />
                        <span className="t-label text-[9px] c-muted">{item.state}</span>
                      </div>
                    </div>
                    <button onClick={() => removeFromCart(item.product_id, item.state)} className="c-muted hover:c-cream transition-colors" data-testid={`remove-item-${i}`}><X className="w-4 h-4" /></button>
                  </div>
                  <div className="flex items-center justify-between mt-4">
                    <div className="flex items-center gap-3">
                      <button onClick={() => updateQuantity(item.product_id, item.state, item.quantity - 1)} className="w-8 h-8 border border-bb-dim flex items-center justify-center c-muted hover:border-bb-gold transition-colors" data-testid={`cart-decrease-${i}`}><Minus className="w-3 h-3" /></button>
                      <span className="t-body c-cream w-6 text-center">{item.quantity}</span>
                      <button onClick={() => updateQuantity(item.product_id, item.state, item.quantity + 1)} className="w-8 h-8 border border-bb-dim flex items-center justify-center c-muted hover:border-bb-gold transition-colors" data-testid={`cart-increase-${i}`}><Plus className="w-3 h-3" /></button>
                    </div>
                    <span className="font-heading text-lg c-cream">${(item.price * item.quantity).toFixed(2)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div>
            <div className="bg-bb-rich border border-bb-border p-6 sticky top-28">
              <h2 className="font-heading text-xl c-cream mb-6">Order Summary</h2>
              <div className="space-y-3 mb-6">
                <div className="flex justify-between"><span className="t-body c-muted text-sm">Subtotal</span><span className="t-body c-cream text-sm">${total.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="t-body c-muted text-sm">Shipping</span><span className="t-body c-cream text-sm">{shipping === 0 ? 'Free' : `$${shipping.toFixed(2)}`}</span></div>
                <div className="border-t border-bb-border pt-3 flex justify-between"><span className="t-label c-cream">Total</span><span className="font-heading text-2xl c-gold">${(total + shipping).toFixed(2)}</span></div>
              </div>

              {total < 50 && (
                <div className="flex items-center gap-2 bg-bb-card border border-bb-border p-3 mb-6">
                  <Truck className="w-4 h-4 c-gold flex-shrink-0" />
                  <span className="t-body text-xs c-muted">Add ${(50 - total).toFixed(2)} for free shipping</span>
                </div>
              )}

              <form onSubmit={handleCheckout} className="space-y-3">
                <p className="t-label c-gold text-[10px] mb-1">Shipping Details</p>
                <input type="text" placeholder="Full Name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full bg-bb-card border border-bb-border px-4 py-3 t-body text-sm c-cream placeholder:c-muted focus:border-bb-gold outline-none transition-colors" required data-testid="checkout-name" />
                <input type="email" placeholder="Email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full bg-bb-card border border-bb-border px-4 py-3 t-body text-sm c-cream placeholder:c-muted focus:border-bb-gold outline-none transition-colors" required data-testid="checkout-email" />
                <input type="tel" placeholder="Phone" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="w-full bg-bb-card border border-bb-border px-4 py-3 t-body text-sm c-cream placeholder:c-muted focus:border-bb-gold outline-none transition-colors" data-testid="checkout-phone" />
                <input type="text" placeholder="Address" value={form.address} onChange={e => setForm({...form, address: e.target.value})} className="w-full bg-bb-card border border-bb-border px-4 py-3 t-body text-sm c-cream placeholder:c-muted focus:border-bb-gold outline-none transition-colors" required data-testid="checkout-address" />
                <div className="grid grid-cols-3 gap-3">
                  <input type="text" placeholder="City" value={form.city} onChange={e => setForm({...form, city: e.target.value})} className="bg-bb-card border border-bb-border px-4 py-3 t-body text-sm c-cream placeholder:c-muted focus:border-bb-gold outline-none transition-colors" required data-testid="checkout-city" />
                  <input type="text" placeholder="State" value={form.state} onChange={e => setForm({...form, state: e.target.value})} className="bg-bb-card border border-bb-border px-4 py-3 t-body text-sm c-cream placeholder:c-muted focus:border-bb-gold outline-none transition-colors" required data-testid="checkout-state" />
                  <input type="text" placeholder="ZIP" value={form.zip} onChange={e => setForm({...form, zip: e.target.value})} className="bg-bb-card border border-bb-border px-4 py-3 t-body text-sm c-cream placeholder:c-muted focus:border-bb-gold outline-none transition-colors" required data-testid="checkout-zip" />
                </div>

                <div className="border-t border-bb-border pt-4 mt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <CreditCard className="w-4 h-4 c-gold" />
                    <p className="t-label c-gold text-[10px]">Payment Details</p>
                    <Lock className="w-3 h-3 c-muted ml-auto" />
                    <span className="t-label text-[9px] c-muted">Encrypted</span>
                  </div>
                  <input type="text" placeholder="Cardholder Name" value={card.name} onChange={e => setCard({...card, name: e.target.value})} className="w-full bg-bb-card border border-bb-border px-4 py-3 t-body text-sm c-cream placeholder:c-muted focus:border-bb-gold outline-none transition-colors mb-3" required data-testid="checkout-card-name" />
                  <input type="text" inputMode="numeric" placeholder="Card Number" value={card.number} onChange={e => setCard({...card, number: formatCardNumber(e.target.value)})} className="w-full bg-bb-card border border-bb-border px-4 py-3 t-body text-sm c-cream placeholder:c-muted focus:border-bb-gold outline-none transition-colors mb-3" required data-testid="checkout-card-number" />
                  <div className="grid grid-cols-2 gap-3">
                    <input type="text" inputMode="numeric" placeholder="MM/YY" value={card.expiry} onChange={e => setCard({...card, expiry: formatExpiry(e.target.value)})} className="bg-bb-card border border-bb-border px-4 py-3 t-body text-sm c-cream placeholder:c-muted focus:border-bb-gold outline-none transition-colors" required data-testid="checkout-card-expiry" />
                    <input type="text" inputMode="numeric" placeholder="CVV" value={card.cvv} onChange={e => setCard({...card, cvv: e.target.value.replace(/\D/g, '').slice(0, 4)})} className="bg-bb-card border border-bb-border px-4 py-3 t-body text-sm c-cream placeholder:c-muted focus:border-bb-gold outline-none transition-colors" required data-testid="checkout-card-cvv" />
                  </div>
                </div>

                <button type="submit" disabled={processing} className="btn-primary w-full mt-4 flex items-center justify-center gap-2" data-testid="checkout-submit">
                  <Lock className="w-4 h-4" />
                  {processing ? 'Submitting Order...' : 'Place Order'}
                </button>
                <p className="t-body text-[10px] c-muted text-center mt-2">Your payment information is encrypted before transmission.</p>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CartPage;
